import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Tooltip, TextField, Button,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DTTagTree from './DTTagTree';
import DTGatewayPicker from './DTGatewayPicker';
import { useDTTagTree } from './hooks/useDTTagTree';
import { useDTGatewaysContext } from './context/DTGatewaysContext';
import { getMaxDatapoints, getDTWidgetSlots } from './registry/dtWidgetTypes';

// ── Device filter picker ──────────────────────────────────────────────────────
function DevicePicker({ devices, selectedDeviceId, onChange, loading, theme }) {
  const isDark = theme === 'dark';
  return (
    <FormControl size="small" fullWidth>
      <InputLabel sx={{ color: isDark ? '#aaa' : undefined, fontSize: 12 }}>Filter by Device</InputLabel>
      <Select
        value={selectedDeviceId || ''}
        label="Filter by Device"
        onChange={(e) => onChange(e.target.value || null)}
        disabled={loading}
        sx={{ color: isDark ? '#fff' : undefined, fontSize: 12 }}
        MenuProps={{ PaperProps: { sx: { backgroundColor: isDark ? '#252525' : undefined } } }}
      >
        <MenuItem value="">
          <em style={{ color: isDark ? '#aaa' : undefined, fontSize: 12 }}>All Devices</em>
        </MenuItem>
        {devices.map((d) => (
          <MenuItem key={d.DeviceID} value={d.DeviceID} sx={{ color: isDark ? '#fff' : undefined, fontSize: 12 }}>
            {d.DeviceName || d.DeviceID}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// ── Slot-based selector for dt-special widgets ───────────────────────────────
// Each slot is a fixed row; user assigns exactly one payload name per slot.

function SlotTagSelector({ slots, selectedTags, onTagsChange, theme, devices, treeLoading, filteredTree, selectedDeviceId, onDeviceChange, gateways, gatewaysLoading, selectedGatewayId, onGatewayChange }) {
  const isDark = theme === 'dark';
  const [activeSlotKey, setActiveSlotKey] = useState(null);
  const [pickerMode, setPickerMode] = useState('manual');
  const [draftPayload, setDraftPayload] = useState('');

  const getTagForSlot = (slotKey) => selectedTags.find((t) => t.slotKey === slotKey);

  const openSlot = useCallback((slotKey) => {
    if (activeSlotKey === slotKey) {
      setActiveSlotKey(null);
      setDraftPayload('');
    } else {
      const existing = selectedTags.find((t) => t.slotKey === slotKey);
      setActiveSlotKey(slotKey);
      setDraftPayload(existing ? String(existing.tagId) : '');
      setPickerMode('manual');
    }
  }, [activeSlotKey, selectedTags]);

  const assignToSlot = useCallback((slotKey, tagId, titleHint, gatewayId) => {
    const id = String(tagId).trim();
    if (!id || !slotKey) return;
    const slot = slots.find((s) => s.key === slotKey);
    const existing = selectedTags.find((t) => t.slotKey === slotKey);
    const newTag = {
      slotKey,
      tagId: id,
      alias: existing?.alias || slot?.label || titleHint || id,
      unit: existing?.unit || slot?.unit || '',
      gatewayId: gatewayId || '',
    };
    const updated = selectedTags.filter((t) => t.slotKey !== slotKey);
    onTagsChange([...updated, newTag]);
    // Auto-advance to next unassigned slot
    const currentIdx = slots.findIndex((s) => s.key === slotKey);
    const nextEmpty = slots.slice(currentIdx + 1).find((s) => !selectedTags.find((t) => t.slotKey === s.key && t.tagId !== id));
    if (nextEmpty) {
      setActiveSlotKey(nextEmpty.key);
      setDraftPayload('');
      setPickerMode('manual');
    } else {
      setActiveSlotKey(null);
      setDraftPayload('');
    }
  }, [slots, selectedTags, onTagsChange]);

  const handleManualAssign = useCallback(() => {
    assignToSlot(activeSlotKey, draftPayload, null);
  }, [activeSlotKey, draftPayload, assignToSlot]);

  const handleTagToggle = useCallback((item) => {
    if (!activeSlotKey) return;
    const existing = getTagForSlot(activeSlotKey);
    if (existing && String(existing.tagId) === String(item.tagId)) {
      onTagsChange(selectedTags.filter((t) => t.slotKey !== activeSlotKey));
      setActiveSlotKey(null);
    } else {
      assignToSlot(activeSlotKey, item.tagId, item.title, item.gatewayId);
    }
  }, [activeSlotKey, selectedTags, onTagsChange, assignToSlot]);

  const handleAliasChange = useCallback((slotKey, alias) => {
    onTagsChange(selectedTags.map((t) => (t.slotKey === slotKey ? { ...t, alias } : t)));
  }, [selectedTags, onTagsChange]);

  const handleRemove = useCallback((slotKey) => {
    onTagsChange(selectedTags.filter((t) => t.slotKey !== slotKey));
    if (activeSlotKey === slotKey) { setActiveSlotKey(null); setDraftPayload(''); }
  }, [selectedTags, onTagsChange, activeSlotKey]);

  const selectedTagIds = selectedTags.map((t) => String(t.tagId));

  // For the browse tree, only mark the active slot's current tag as checked.
  // Passing all-slots IDs + maxDatapoints=1 would disable every unselected tag
  // the moment any slot is filled, preventing reassignment.
  const activeSlotTagIds = useMemo(() => {
    if (!activeSlotKey) return [];
    const tag = getTagForSlot(activeSlotKey);
    return tag ? [String(tag.tagId)] : [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlotKey, selectedTags]);

  const cellSx = { padding: '4px 6px', borderColor: isDark ? '#333' : '#eee', verticalAlign: 'middle' };
  const inputSx = {
    '& .MuiInputBase-input': { color: isDark ? '#fff' : '#000', fontSize: 11, padding: '2px 4px' },
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
      '& fieldset': { borderColor: isDark ? '#444' : '#ccc' },
      '&.Mui-focused fieldset': { borderColor: '#008CD7' },
    },
  };

  return (
    <Box>
      <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
        Slot Mapping — {selectedTags.length}/{slots.length} assigned
      </Typography>
      <Typography variant="caption" sx={{ color: isDark ? '#666' : '#999', display: 'block', mb: 1.5, fontSize: 10 }}>
        Click a slot row to assign a payload name. Each slot maps to exactly one tag.
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            {['Widget Section', 'Payload Name', 'Display Name', ''].map((h, i) => (
              <TableCell key={i} sx={{ ...cellSx, color: isDark ? '#888' : '#555', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {slots.map((slot) => {
            const assigned = getTagForSlot(slot.key);
            const isActive = activeSlotKey === slot.key;

            return (
              <React.Fragment key={slot.key}>
                {/* ── Main slot row ── */}
                <TableRow
                  onClick={() => openSlot(slot.key)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isActive
                      ? (isDark ? '#0d2a45' : '#e3f2fd')
                      : assigned
                      ? (isDark ? '#0d2010' : '#f1f8e9')
                      : 'transparent',
                    '&:hover': { backgroundColor: isDark ? '#1e1e1e' : '#f5f5f5' },
                    borderLeft: isActive ? '3px solid #008CD7' : assigned ? '3px solid #4caf50' : `3px solid ${isDark ? '#333' : '#e0e0e0'}`,
                  }}
                >
                  <TableCell sx={{ ...cellSx, fontWeight: 600, fontSize: 11, color: isDark ? '#ddd' : '#333', width: '35%' }}>
                    {slot.label}
                    {slot.unit && (
                      <Typography component="span" sx={{ fontSize: 9, color: isDark ? '#666' : '#aaa', ml: 0.5 }}>
                        ({slot.unit})
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ ...cellSx, width: '30%' }}>
                    {assigned ? (
                      <Typography sx={{ fontSize: 11, color: isDark ? '#90caf9' : '#1565c0', fontFamily: 'monospace', fontWeight: 600 }}>
                        {assigned.tagId}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 10, color: isDark ? '#444' : '#bbb', fontStyle: 'italic' }}>
                        {isActive ? 'enter below ↓' : '— not assigned —'}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ ...cellSx, width: '28%' }} onClick={(e) => e.stopPropagation()}>
                    {assigned && (
                      <TextField
                        size="small"
                        value={assigned.alias}
                        onChange={(e) => handleAliasChange(slot.key, e.target.value)}
                        variant="outlined"
                        sx={{ ...inputSx, width: '100%' }}
                        inputProps={{ style: { padding: '2px 6px', fontSize: 11 } }}
                      />
                    )}
                  </TableCell>

                  <TableCell sx={{ ...cellSx, width: '7%', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {assigned && (
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => handleRemove(slot.key)}>
                          <DeleteIcon sx={{ fontSize: 13, color: '#f44336' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>

                {/* ── Inline assignment panel (expands under active row) ── */}
                {isActive && (
                  <TableRow sx={{ backgroundColor: isDark ? '#0a1929' : '#f0f7ff' }}>
                    <TableCell colSpan={4} sx={{ padding: '8px 12px', borderColor: isDark ? '#1a3a5c' : '#bbdefb', borderLeft: '3px solid #008CD7' }}>
                      {/* Mode toggle */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Button
                          size="small"
                          variant={pickerMode === 'manual' ? 'contained' : 'outlined'}
                          onClick={(e) => { e.stopPropagation(); setPickerMode('manual'); }}
                          sx={{ textTransform: 'none', fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                        >
                          Enter payload name
                        </Button>
                        <Button
                          size="small"
                          variant={pickerMode === 'browse' ? 'contained' : 'outlined'}
                          onClick={(e) => { e.stopPropagation(); setPickerMode('browse'); }}
                          sx={{ textTransform: 'none', fontSize: 10, py: 0.25, px: 1, minWidth: 0 }}
                        >
                          Browse tags
                        </Button>
                      </Box>

                      {pickerMode === 'manual' ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <TextField
                            size="small"
                            autoFocus
                            fullWidth
                            placeholder={`Payload name for "${slot.label}"`}
                            value={draftPayload}
                            onChange={(e) => setDraftPayload(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleManualAssign(); e.stopPropagation(); }}
                            sx={inputSx}
                            inputProps={{ style: { padding: '4px 8px', fontSize: 12 } }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => { e.stopPropagation(); handleManualAssign(); }}
                            disabled={!draftPayload.trim()}
                            startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                            sx={{ textTransform: 'none', fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            Assign
                          </Button>
                        </Box>
                      ) : (
                        <Box onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ mb: 1 }}>
                            <DTGatewayPicker
                              gateways={gateways}
                              selectedGatewayId={selectedGatewayId}
                              onChange={onGatewayChange}
                              loading={gatewaysLoading}
                              theme={theme}
                            />
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <DevicePicker
                              devices={devices}
                              selectedDeviceId={selectedDeviceId}
                              onChange={onDeviceChange}
                              loading={treeLoading}
                              theme={theme}
                            />
                          </Box>
                          <DTTagTree
                            tree={filteredTree}
                            loading={treeLoading}
                            selectedTagIds={activeSlotTagIds}
                            onTagToggle={handleTagToggle}
                            maxDatapoints={slots.length}
                            theme={theme}
                          />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

const GAUGE_TYPES_SET = new Set(['Circular Gauge', 'Semi Circular Gauge', 'Linear Gauge', 'Speed Gauge', 'Temperature Gauge']);
const CHART_TYPES_SET = new Set(['Line Chart', 'Bar Chart', 'Area Chart', 'Pie Chart', 'Radar Chart', 'Doughnut Chart', 'Scatter Chart']);
const UNIT_TYPES_SET = new Set(['Multi Value', 'Timeseries Table', ...GAUGE_TYPES_SET, ...CHART_TYPES_SET]);

// ── Free-form selector for general widgets ───────────────────────────────────

function FreeTagSelector({ widgetType, selectedTags, onTagsChange, theme, devices, treeLoading, filteredTree, selectedDeviceId, onDeviceChange, gateways, gatewaysLoading, selectedGatewayId, onGatewayChange }) {
  const isDark = theme === 'dark';
  const maxDatapoints = getMaxDatapoints(widgetType);
  const selectedTagIds = selectedTags.map((t) => String(t.tagId));

  const showUnit = UNIT_TYPES_SET.has(widgetType);
  const showMinMax = GAUGE_TYPES_SET.has(widgetType) || CHART_TYPES_SET.has(widgetType);

  const [manualMode, setManualMode] = useState(false);
  const [manualTagId, setManualTagId] = useState('');

  const handleManualAdd = useCallback(() => {
    const id = manualTagId.trim();
    if (!id || selectedTags.length >= maxDatapoints) return;
    if (selectedTags.some((t) => String(t.tagId) === id)) return;
    onTagsChange([
      ...selectedTags,
      { tagId: id, alias: id, unit: '', min: '', max: '' },
    ]);
    setManualTagId('');
  }, [manualTagId, selectedTags, maxDatapoints, onTagsChange]);

  const handleTagToggle = useCallback(
    (item) => {
      const idStr = String(item.tagId);
      const existing = selectedTags.find((t) => String(t.tagId) === idStr);
      if (existing) {
        onTagsChange(selectedTags.filter((t) => String(t.tagId) !== idStr));
      } else {
        if (selectedTags.length < maxDatapoints) {
          onTagsChange([
            ...selectedTags,
            {
              tagId: item.tagId,
              alias: item.title,
              unit: item.unit || '',
              gatewayId: item.gatewayId || '',
              min: '',
              max: '',
            },
          ]);
        }
      }
    },
    [selectedTags, maxDatapoints, onTagsChange]
  );

  const handleFieldChange = useCallback(
    (tagId, field, value) => {
      onTagsChange(selectedTags.map((t) => (String(t.tagId) === String(tagId) ? { ...t, [field]: value } : t)));
    },
    [selectedTags, onTagsChange]
  );

  const handleRemove = useCallback(
    (tagId) => {
      onTagsChange(selectedTags.filter((t) => String(t.tagId) !== String(tagId)));
    },
    [selectedTags, onTagsChange]
  );

  const inputSx = (width = 70) => ({
    '& .MuiInput-input': { fontSize: 11, color: isDark ? '#fff' : '#000' },
    width,
  });
  const cellSx = { padding: '2px 4px', borderColor: isDark ? '#333' : '#eee' };
  const headerCellSx = { color: isDark ? '#888' : '#555', fontSize: 11, padding: '2px 6px', borderColor: isDark ? '#333' : '#eee' };

  const headers = ['Tag ID', 'Display Name', ...(showUnit ? ['Unit'] : []), ...(showMinMax ? ['Min', 'Max'] : []), ''];

  return (
    <Box>
      <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
        Tag Mapping ({selectedTags.length}/{maxDatapoints} selected)
      </Typography>

      {/* Manual / Picker toggle */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <Button
          size="small"
          variant={!manualMode ? 'contained' : 'outlined'}
          onClick={() => setManualMode(false)}
          sx={{ textTransform: 'none', fontSize: 11, py: 0.25, minWidth: 0 }}
        >
          Browse
        </Button>
        <Button
          size="small"
          variant={manualMode ? 'contained' : 'outlined'}
          onClick={() => setManualMode(true)}
          startIcon={<EditIcon sx={{ fontSize: 13 }} />}
          sx={{ textTransform: 'none', fontSize: 11, py: 0.25, minWidth: 0 }}
        >
          Enter manually
        </Button>
      </Box>

      {manualMode ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            label="Payload name / Tag ID"
            placeholder="e.g. sensor_voltage_r"
            value={manualTagId}
            onChange={(e) => setManualTagId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleManualAdd(); }}
            disabled={selectedTags.length >= maxDatapoints}
            sx={{
              '& .MuiInputBase-input': { color: isDark ? '#fff' : '#000', fontSize: 12 },
              '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#555', fontSize: 12 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? '#252525' : '#fafafa',
                '& fieldset': { borderColor: isDark ? '#444' : '#ccc' },
              },
            }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={handleManualAdd}
            disabled={!manualTagId.trim() || selectedTags.length >= maxDatapoints}
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', fontSize: 11, whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 1.5 }}>
            <DTGatewayPicker
              gateways={gateways}
              selectedGatewayId={selectedGatewayId}
              onChange={onGatewayChange}
              loading={gatewaysLoading}
              theme={theme}
            />
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <DevicePicker
              devices={devices}
              selectedDeviceId={selectedDeviceId}
              onChange={onDeviceChange}
              loading={treeLoading}
              theme={theme}
            />
          </Box>
          <DTTagTree
            tree={filteredTree}
            loading={treeLoading}
            selectedTagIds={selectedTagIds}
            onTagToggle={handleTagToggle}
            maxDatapoints={maxDatapoints}
            theme={theme}
          />
        </>
      )}

      {selectedTags.length > 0 && (
        <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
          <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', display: 'block', mb: 0.5, fontWeight: 600 }}>
            Selected Tags
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {headers.map((h, i) => (
                  <TableCell key={i} sx={headerCellSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedTags.map((t) => (
                <TableRow key={t.tagId}>
                  <TableCell sx={{ ...cellSx, color: isDark ? '#aaa' : '#666', fontSize: 11 }}>
                    {t.tagId}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      value={t.alias}
                      onChange={(e) => handleFieldChange(t.tagId, 'alias', e.target.value)}
                      variant="standard"
                      placeholder="Name"
                      sx={inputSx(90)}
                    />
                  </TableCell>
                  {showUnit && (
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        value={t.unit || ''}
                        onChange={(e) => handleFieldChange(t.tagId, 'unit', e.target.value)}
                        variant="standard"
                        placeholder="e.g. V"
                        sx={inputSx(44)}
                      />
                    </TableCell>
                  )}
                  {showMinMax && (
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        value={t.min ?? ''}
                        onChange={(e) => handleFieldChange(t.tagId, 'min', e.target.value)}
                        variant="standard"
                        placeholder="0"
                        type="number"
                        sx={inputSx(44)}
                      />
                    </TableCell>
                  )}
                  {showMinMax && (
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        value={t.max ?? ''}
                        onChange={(e) => handleFieldChange(t.tagId, 'max', e.target.value)}
                        variant="standard"
                        placeholder="100"
                        type="number"
                        sx={inputSx(44)}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={cellSx}>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleRemove(t.tagId)}>
                        <DeleteIcon sx={{ fontSize: 14, color: '#f44336' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// ── Main DTTagSelector ───────────────────────────────────────────────────────

function DTTagSelector({ widgetType, selectedTags, onTagsChange, theme }) {
  const [selectedGatewayId, setSelectedGatewayId] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);

  const { allTags, devices, loading: treeLoading } = useDTTagTree();
  const { gateways, loading: gatewaysLoading } = useDTGatewaysContext();

  // Selecting a gateway resets the device filter to avoid stale cross-gateway selection
  const handleGatewayChange = useCallback((gwId) => {
    setSelectedGatewayId(gwId);
    setSelectedDeviceId(null);
  }, []);

  // Devices filtered by selected gateway
  const filteredDevices = useMemo(() => {
    if (!selectedGatewayId) return devices;
    return devices.filter((d) => d.GatewayID === selectedGatewayId);
  }, [devices, selectedGatewayId]);

  // Tags filtered by gateway then by device
  const filteredTree = useMemo(() => {
    let tags = allTags;
    if (selectedGatewayId) tags = tags.filter((t) => t.gatewayId === selectedGatewayId);
    if (selectedDeviceId) tags = tags.filter((t) => t.deviceId === selectedDeviceId);
    return tags;
  }, [allTags, selectedGatewayId, selectedDeviceId]);

  const slots = getDTWidgetSlots(widgetType);
  const sharedProps = {
    theme,
    devices: filteredDevices,
    treeLoading,
    filteredTree,
    selectedDeviceId,
    onDeviceChange: setSelectedDeviceId,
    gateways,
    gatewaysLoading,
    selectedGatewayId,
    onGatewayChange: handleGatewayChange,
    selectedTags,
    onTagsChange,
  };

  if (slots) {
    return <SlotTagSelector slots={slots} widgetType={widgetType} {...sharedProps} />;
  }

  return <FreeTagSelector widgetType={widgetType} {...sharedProps} />;
}

export default React.memo(DTTagSelector);
